import InfiniteComboboxMulti from 'components/InfiniteCombobox/InfiniteComboboxMulti';
import { FeConstants } from 'constants/FeConstants';
import { getProspectName } from 'crono-fe-common/utils';
import { useSearchProspectsInfiniteScroll } from 'hooks/services/prospect/useSearchProspects';
import { useMemo, useState } from 'react';

interface AttendeesComboboxProps {
  id?: string;
  accountId: string | null;
  disabled?: boolean;
  values: string[];
  onChange: (emails: string[]) => void;
}

// Multi-select of attendee emails. Options come from the account's contacts
// (server search + infinite scroll), but the persisted value is a plain
// string[] of emails and chips render the email — so a seeded email with n
// matching contact still shows.
const AttendeesCombobox = ({
  id,
  accountId,
  disabled,
  values,
  onChange,
}: AttendeesComboboxProps) => {
  const [search, setSearch] = useState('');

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useSearchProspectsInfiniteScroll(
      {
        accountId,
        name: search || null,
        limit: FeConstants.contactPageLimit,
        sort: 'Name',
      },
      false,
    );

  const labelByEmail = useMemo(() => {
    const prospects = data?.pages.flatMap((page) => page.data.data) ?? [];
    const result = new Map<string, string>();

    for (const prospect of prospects) {
      const email = prospect.email;
      if (!email || result.has(email)) {
        continue;
      }
      const name = getProspectName(prospect);
      result.set(email, name ? `${name} (${email})` : email);
    }

    return result;
  }, [data]);

  const options = useMemo(() => [...labelByEmail.keys()], [labelByEmail]);

  return (
    <InfiniteComboboxMulti<string>
      id={id}
      variant="inline"
      disabled={disabled}
      values={values}
      onChange={onChange}
      options={options}
      getOptionKey={(email) => email}
      getOptionLabel={(email) => labelByEmail.get(email) ?? email}
      renderChip={(email) => email}
      onSearchChange={setSearch}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={isLoading}
      dsSize="sm"
      triggerClassName="w-full"
      placeholder="Add attendees"
      empty="No contacts found"
      loadingLabel="Loading contacts"
      loadingMoreLabel="Loading more contacts"
    />
  );
};

export default AttendeesCombobox;
